import { AvatarBadge } from '@/components/ui/avatar';
import { useRealtimeStatus } from '@/core/realtime';
import { cn } from '@/lib/utils';
import { lastSeenLabel } from '../lastSeen';
import { usePresence, usePresenceSettings } from '../queries';

function Dot({ online, label, className }: { online: boolean; label: string; className?: string }) {
  return (
    <AvatarBadge
      role="img"
      aria-label={label}
      title={label}
      className={cn(online ? 'bg-emerald-500' : 'bg-muted-foreground/50', className)}
    />
  );
}

/**
 * Online/offline dot for someone else's avatar (FE-ADR-068), by member id or
 * user id. Shows nothing to guests, or when status can't be known.
 */
export function PresenceBadge({
  member,
  user,
  className,
}: {
  member?: number | null;
  user?: number | null;
  className?: string;
}) {
  const presence = usePresence({ member, user });
  if (!presence) return null;
  const label = presence.online ? 'Online' : lastSeenLabel(presence.lastSeenAt);
  return <Dot online={presence.online} label={label} className={className} />;
}

/** The caller's own dot: what others see — online while the socket is up,
 *  grey while they appear offline. */
export function SelfPresenceBadge({ className }: { className?: string }) {
  const status = useRealtimeStatus();
  const { data: settings } = usePresenceSettings();
  if (status !== 'connected' || !settings) return null;
  return settings.appear_offline ? (
    <Dot online={false} label="Appearing offline" className={className} />
  ) : (
    <Dot online label="Online" className={className} />
  );
}
